import { useQuery } from '@tanstack/react-query';
import { api, type SessionUser } from '../api/client.js';
import { OrgCanvas } from '../components/OrgCanvas.js';

/**
 * ทั้งระบบเหลือหน้าเดียว
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมถึงยุบเมนูเจ็ดอันทิ้ง
 * ────────────────────────────────────────────────────────────────
 *
 * เดิมเป็นเจ็ดหน้าแยกกันในเมนูซ้าย ซึ่งบังคับให้ผู้บริหารต้องรู้ล่วงหน้าว่า
 * คำถามที่ตัวเองมีอยู่ในหน้าไหน ก่อนจะเริ่มหาคำตอบได้
 * แต่คำถามจริงของเขาไม่ได้มาเป็นหน้า มันมาเป็นหน่วยงาน — "กรมนี้กำลังคนเป็นยังไง"
 * แล้วคำถามถัดไปทุกข้อก็ยังเป็นหน่วยงานเดิม
 *
 * โครงนี้จึงกลับด้าน ให้ผังเป็นตัวยืน แล้วคำถามทั้งหมดเป็นมุมมองของผังใบเดียวกัน
 * ผู้ใช้ไม่ต้องจำว่าอะไรอยู่ที่ไหน เพราะไม่มี "ที่ไหน" ให้จำอีกแล้ว
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ หน้าที่ถูกยกออกจากเมนู — ไฟล์ยังอยู่ครบ ไม่ได้ลบ
 * ────────────────────────────────────────────────────────────────
 *
 *   pages/Dashboard.tsx     ภาพรวมแบบเลื่อนเป็นหัวข้อ
 *   pages/OrgExplorer.tsx   โครงสร้างและกำลังคน (ค้นหา + แผงลากได้)
 *   pages/Ask.tsx           ถาม-ตอบด้วย AI
 *   pages/People.tsx        รายชื่อทั้งหมด
 *   pages/CareerPath.tsx    เส้นทางก้าวหน้า
 *   pages/TalentFlow.tsx    การเลื่อนที่ผ่านมา
 *   pages/Authority.tsx     ใครเป็นคนลงนามแต่งตั้ง
 *   pages/PersonCard.tsx    การ์ดประวัติรายบุคคล
 *   pages/Eligibility.tsx · pages/Heatmap.tsx · pages/Simulation.tsx
 *
 * ทุกไฟล์ยัง typecheck และ build ผ่านตามปกติ และ API ที่มันเรียกยังทำงานครบทุกเส้น
 * เก็บไว้แบบนี้ตามแบบเดียวกับ Simulation.tsx ที่เคยถูกยกออกจากเมนูมาก่อน
 * เพราะโค้ดที่ถูก comment ทิ้งหรือถูกลบ คือโค้ดที่ไม่มีใคร typecheck ให้อีกเลย
 * วันที่จะเอากลับมาจะพบว่ามันพังไปแล้วโดยไม่มีใครรู้ว่าพังตั้งแต่เมื่อไร
 *
 * จะเอาหน้าไหนกลับ: import แล้วเรนเดอร์เพิ่มใน App.tsx ตามเส้นทางที่ router.ts มีให้อยู่แล้ว
 * — ไม่ต้องรื้ออะไรเพิ่ม
 */
export function Console({ user, onLogout }: { user: SessionUser; onLogout: () => void }) {
  const summary = useQuery({ queryKey: ['summary'], queryFn: api.summary });

  /**
   * ใช้ผังแบบเต็ม (มีตำแหน่งรายอัตราติดมาด้วย) เพราะผังต้องกางลงไปถึงระดับตำแหน่งได้
   * payload ราว 500 KB เทียบกับแบบไม่มีตำแหน่ง 29 KB — ยอมแลกเพราะโหลดครั้งเดียวแล้ว cache ไว้
   * และตอนนี้ผังคือทั้งระบบ ไม่ใช่กล่องหนึ่งในหน้าที่ผู้ใช้อาจไม่เลื่อนลงมาดูเลย
   */
  const tree = useQuery({ queryKey: ['org-tree-full'], queryFn: api.orgTreeFull });

  if (summary.isPending || tree.isPending) {
    return <p className="state boot">กำลังโหลดข้อมูลกำลังคน…</p>;
  }

  if (summary.isError || tree.isError || !summary.data || !tree.data) {
    return (
      <p className="state error boot">
        โหลดข้อมูลไม่สำเร็จ — {(summary.error ?? tree.error)?.message ?? 'ไม่ทราบสาเหตุ'}
      </p>
    );
  }

  return <OrgCanvas tree={tree.data} summary={summary.data} user={user} onLogout={onLogout} />;
}
